'use client';

/**
 * EntitiesExplorer — the page-level client boundary for `/<chain>/entities` that
 * UNIFIES selection between the EntityGraph (canvas + inspector) and the
 * Directory/INDEX (search + dense table) into ONE inspector (Lam, 2026-06-09:
 * "one preview, not two" — the darkening EntityPreviewDrawer is retired).
 *
 * Layout (map inversion): the GRAPH is the page's visual source of truth, then the
 * "NEW ON THE MAP" strip of recent graph arrivals, then THE INDEX table.
 *
 * Selection model: this component owns `selectedId` — an entity SLUG or an asset
 * node id (`asset:{type}_{id}`, graph-only). A graph-node click, an index-row
 * click AND a new-on-the-map chip click all write the same state, so they select
 * into the SAME inspector and cross-highlight each other (the graph focuses/halos
 * the node; the index row gets its inset gold spine). A directory/strip selection
 * also scrolls the graph section into view on ≥768px viewports.
 *
 * SEO: this island renders the same SSR'd children the page rendered before —
 * the Directory's crawlable `/tags/[slug]` links are untouched.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import EntityGraph from './EntityGraph';
import Directory from './Directory';
import { Avatar, Mono } from './atoms';
import type {
  ChainAssetNode,
  ChainEntity,
  ChainTypedEdge,
  EntitySearchRow,
} from '@/lib/hub/chain-entities-data';

/** Recency window for the "NEW ON THE MAP" strip (days). */
const NEW_WINDOW_DAYS = 30;
/** Max chips shown in the strip. */
const NEW_CHIP_CAP = 4;

export default function EntitiesExplorer({
  entities,
  guests = [],
  typedEdges,
  assets,
  offChainIndex,
  chainColor,
  chainLabel,
  initialFocusSlug = null,
  initialPath = null,
}: {
  entities: ChainEntity[];
  /** Off-chain GUEST nodes (1-hop typed-edge, `offChain` set) — graph-only:
      passed to EntityGraph, NEVER to the Directory (members stay the count). */
  guests?: ChainEntity[];
  typedEdges: ChainTypedEdge[];
  assets: ChainAssetNode[];
  offChainIndex: EntitySearchRow[];
  chainColor: string;
  chainLabel: string;
  /** `?focus=<slug>` deep-link seed (from an entity page's "View in graph →").
      Pre-selects that node so the graph opens haloed on it with the inspector
      open. Only seeds the INITIAL selection — user clicks override it freely. */
  initialFocusSlug?: string | null;
  /** `?path=slugA,slugB` deep-link seed (the path-finder share link) — opens the
      graph in path mode with both endpoints chosen. Forwarded to EntityGraph. */
  initialPath?: readonly [string, string] | null;
}) {
  // The ONE selection both surfaces read/write (entity slug | asset node id | null).
  // Seeded from the ?focus= deep-link when it names a real in-chain entity.
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    if (initialFocusSlug && entities.some((e) => e.slug === initialFocusSlug)) {
      return initialFocusSlug;
    }
    return null;
  });
  const graphWrap = useRef<HTMLDivElement>(null);

  const selectFromDirectory = useCallback((slug: string) => {
    setSelectedId(slug);
    // Bring the inspector (rail beside/below the canvas) into view. Skipped on
    // phones, where the inspector opens as a fixed bottom sheet over the canvas.
    if (typeof window !== 'undefined' && window.innerWidth >= 768) {
      graphWrap.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // The directory only highlights ENTITY selections (asset ids are graph-only).
  const selectedSlug = selectedId && !selectedId.startsWith('asset:') ? selectedId : null;

  // NEW ON THE MAP — the newest graph arrivals, derived from confirmed edges'
  // createdAt. Each entrant = an endpoint of a recently-added edge; deduped,
  // newest first, within a 30d window, capped at 4 (see NewOnTheMap).
  const newEntrants = useMemo(
    () => computeNewEntrants(typedEdges, entities, guests),
    [typedEdges, entities, guests],
  );

  // Δ30d per-entity: count of typed edges added in the last 30d touching each
  // member slug (from edge createdAt). Powers the index's honest `+N` marker; an
  // edge with no createdAt or one outside the window contributes nothing.
  const edgesAdded30d = useMemo(() => computeEdgesAdded30d(typedEdges), [typedEdges]);

  return (
    <>
      <div ref={graphWrap} className="scroll-mt-20">
        <EntityGraph
          entities={entities}
          guests={guests}
          typedEdges={typedEdges}
          assets={assets}
          chainColor={chainColor}
          chainLabel={chainLabel}
          selectedId={selectedId}
          onSelectId={setSelectedId}
          initialPath={initialPath}
        />
      </div>
      {newEntrants.length > 0 && (
        <NewOnTheMap entrants={newEntrants} onSelect={selectFromDirectory} />
      )}
      <Directory
        entities={entities}
        offChainIndex={offChainIndex}
        edgesAdded30d={edgesAdded30d}
        selectedSlug={selectedSlug}
        onSelect={selectFromDirectory}
      />
    </>
  );
}

/* ── NEW ON THE MAP ──────────────────────────────────────────────────────────
   A recent-arrivals strip between the graph and the index (map.html zone 2): up
   to 4 mini-chips of the entities at the endpoints of the newest confirmed edges.
   Source = edge `createdAt` (surfaced on the projection). If no edge carries a
   createdAt, computeNewEntrants returns [] and the whole strip renders nothing. */

interface NewEntrant {
  slug: string;
  name: string;
  type: string;
  image?: string;
  /** How many newly-added edges (within the window) touch this entity. */
  edgeCount: number;
  /** ISO of the newest edge touching this entity (drives the date + the sort). */
  newestIso: string;
}

/**
 * Endpoints of the newest confirmed edges, deduped and newest-first.
 * Only members + admitted guests resolve (the same set the graph draws). An edge
 * without a createdAt contributes nothing — so a chain whose edges predate the
 * field yields an EMPTY strip (rendered as nothing, by design).
 */
function computeNewEntrants(
  typedEdges: ChainTypedEdge[],
  entities: ChainEntity[],
  guests: ChainEntity[],
): NewEntrant[] {
  const bySlug = new Map([...entities, ...guests].map((e) => [e.slug, e]));
  const cutoff = Date.now() - NEW_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const acc = new Map<string, NewEntrant>();

  for (const te of typedEdges) {
    if (!te.createdAt) continue;
    const t = new Date(te.createdAt).getTime();
    if (Number.isNaN(t) || t < cutoff) continue;
    for (const slug of [te.fromSlug, te.toSlug]) {
      const e = bySlug.get(slug);
      if (!e) continue;
      const existing = acc.get(slug);
      if (existing) {
        existing.edgeCount += 1;
        if (te.createdAt > existing.newestIso) existing.newestIso = te.createdAt;
      } else {
        acc.set(slug, {
          slug: e.slug,
          name: e.name,
          type: e.type,
          image: e.image,
          edgeCount: 1,
          newestIso: te.createdAt,
        });
      }
    }
  }

  return [...acc.values()]
    .sort((a, b) => (a.newestIso < b.newestIso ? 1 : a.newestIso > b.newestIso ? -1 : 0))
    .slice(0, NEW_CHIP_CAP);
}

/**
 * Per-entity-slug count of typed edges created in the last NEW_WINDOW_DAYS days.
 * Honest Δ30d source: an edge with no createdAt — or one outside the window —
 * contributes nothing, so a chain whose edges predate the field yields an empty
 * map (no `+N` markers at all). Counts boundary edges for their member endpoint(s)
 * just like the graph's drawn-edge degree.
 */
function computeEdgesAdded30d(typedEdges: ChainTypedEdge[]): Map<string, number> {
  const cutoff = Date.now() - NEW_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const out = new Map<string, number>();
  for (const te of typedEdges) {
    if (!te.createdAt) continue;
    const t = new Date(te.createdAt).getTime();
    if (Number.isNaN(t) || t < cutoff) continue;
    out.set(te.fromSlug, (out.get(te.fromSlug) ?? 0) + 1);
    out.set(te.toSlug, (out.get(te.toSlug) ?? 0) + 1);
  }
  return out;
}

/** "Jun 10"-style short date for a chip. */
function chipDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function NewOnTheMap({
  entrants,
  onSelect,
}: {
  entrants: NewEntrant[];
  onSelect: (slug: string) => void;
}) {
  return (
    <section
      aria-label="New on the map"
      className="mt-4 flex flex-col items-stretch gap-0 overflow-hidden rounded border-[0.5px] border-divider bg-ink sm:flex-row"
    >
      <div className="flex flex-shrink-0 flex-col justify-center gap-0.5 border-b-[0.5px] border-divider px-4 py-3 sm:min-w-[184px] sm:border-b-0 sm:border-r-[0.5px]">
        <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-scout-gold">
          New on the map
        </span>
        <Mono size={10} color="var(--parchment-dim)">
          Recent entrants &amp; edges
        </Mono>
      </div>
      <div className="grid flex-1 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {entrants.map((e) => (
          <button
            key={e.slug}
            type="button"
            onClick={() => onSelect(e.slug)}
            className="flex items-center gap-3 border-b-[0.5px] border-divider px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-parchment/[0.025] sm:border-b-0 sm:border-r-[0.5px] sm:last:border-r-0"
          >
            {e.image ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={e.image}
                alt=""
                loading="lazy"
                className="h-[30px] w-[30px] flex-shrink-0 rounded object-cover"
              />
            ) : (
              <Avatar name={e.name} type={e.type} size={30} />
            )}
            <span className="min-w-0">
              <span className="block truncate font-display text-[13px] font-bold text-parchment">
                {e.name}
              </span>
              <span className="mt-0.5 block font-mono text-[9.5px] tracking-[0.03em] text-parchment-dim">
                <span className="text-scout-gold">
                  +{e.edgeCount} {e.edgeCount === 1 ? 'edge' : 'edges'}
                </span>{' '}
                · {chipDate(e.newestIso)}
              </span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
